/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';
import {ErrorType, ResponseData, validateEmail} from '@dvrd/dvr-controls';
import {AppContext} from '../../misc/appContext';
import TestMail from './testMail';
import {ActivateHandle, PushDevice} from '../../../util/interfaces';
import {listDeviceTokens} from '../../../util/requestSender';
import {useTranslation} from 'react-i18next';

interface Props {
    onSubmit: (email: string) => void;
    closeOnSubmit?: boolean;
}

export type TestMailRef = ActivateHandle<string | null | undefined> & {
    setLoading: (value: boolean) => void;
    onClose: VoidFunction;
};

function TestMailController(props: Props, ref: ForwardedRef<TestMailRef>) {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const {onSubmit, closeOnSubmit} = props;
    const [active, setActive] = useState(false);
    const [email, setEmail] = useState('');
    const [error, setError] = useState<ErrorType>(null);
    const [loading, setLoading] = useState(false);
    const [pushDevices, setPushDevices] = useState<Array<PushDevice>>([]);
    const [hasPushChannel, setHasPushChannel] = useState(false);
    const [hasEmailChannel, setHasEmailChannel] = useState(true);
    const [title, setTitle] = useState<string | undefined>(undefined);

    function onActivate(email?: string, channels?: Array<string>, customTitle?: string) {
        setActive(true);
        setTitle(customTitle);
        setEmail(email ?? context.userContext.user?.email ?? '');
        // No channels passed / legacy empty list = e-mail only (the historic
        // default) — mirrors readNotificationChannels' fallback.
        const normalized = channels?.length ? channels : ['EMAIL'];
        const wantsPush = normalized.includes('WEB_PUSH');
        setHasPushChannel(wantsPush);
        setHasEmailChannel(normalized.includes('EMAIL'));
        const accountId = context.accountContext.account?.id;
        if (wantsPush && accountId) {
            listDeviceTokens({
                account_id: accountId,
                callback: (data: ResponseData) => {
                    if (data.success) {
                        const rows: Array<PushDevice> = data.tokens || data.device_tokens || [];
                        setPushDevices(rows);
                    }
                }
            });
        } else {
            setPushDevices([]);
        }
    }

    function onClose() {
        setActive(false);
        setEmail('');
        setLoading(false);
        setError(null);
        setPushDevices([]);
        setHasPushChannel(false);
        setHasEmailChannel(true);
        setTitle(undefined);
    }

    function onChangeEmail(email: string) {
        setEmail(email);
    }

    function _onSubmit() {
        // With the e-mail channel off, the address is not shown/used for this
        // test — only the push targets — so it must not block the submit.
        if (hasEmailChannel && !validateEmail(email)) {
            setError(t('email.test.invalidEmail', 'Vul een geldig e-mailadres in'));
            return;
        }
        onSubmit(email);
        if (closeOnSubmit) onClose();
    }

    function onResetError() {
        setError(null);
    }

    useImperativeHandle(ref, () => ({activate: onActivate, setLoading, onClose}));

    return (
        <TestMail
            onChange={onChangeEmail}
            onSubmit={_onSubmit}
            onClose={onClose}
            onResetError={onResetError}
            error={error}
            active={active}
            email={email}
            loading={loading}
            pushDevices={hasPushChannel ? pushDevices : undefined}
            showEmail={hasEmailChannel}
            title={title}
        />
    );
}

export default forwardRef<TestMailRef, Props>(TestMailController);
